import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLink } from "@/components/Brand";
import {
  ArrowLeft, Plus, Trash2, Wrench as WrenchIcon, Package,
  Search, LogOut, Loader2, Lock, AlertTriangle, Video
} from "lucide-react";
import { toast } from "sonner";

const URGENCIES = [
  { v: "green", label: "Good", color: "bg-success/15 text-success border-success/30" },
  { v: "amber", label: "Advisory", color: "bg-warning/15 text-warning border-warning/30" },
  { v: "red", label: "Requires attention", color: "bg-destructive/15 text-destructive border-destructive/30" },
];

export default function CatalogPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({
    name: "", type: "part", description: "", default_quantity: 1,
    default_price: 0, urgency: "amber", category: "", explainer_url: "",
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/repair-templates");
      setItems(r.data);
    } catch { toast.error("Failed to load catalog"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/repair-templates", {
        ...draft,
        default_quantity: Number(draft.default_quantity),
        default_price: Number(draft.default_price),
      });
      toast.success("Added to your catalog");
      setShowNew(false);
      setDraft({ name: "", type: "part", description: "", default_quantity: 1, default_price: 0, urgency: "amber", category: "", explainer_url: "" });
      refresh();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/repair-templates/${id}`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot delete (global items are read-only)"); }
  };

  const filtered = items.filter(i => {
    if (!q) return true;
    const s = q.toLowerCase();
    return i.name.toLowerCase().includes(s) || (i.category || "").toLowerCase().includes(s);
  });

  const grouped = filtered.reduce((acc, it) => {
    const k = it.category || "Uncategorised";
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 h-16 flex items-center justify-between">
          <BrandLink />
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="btn-outline" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <button onClick={() => { logout(); nav("/"); }} className="btn-outline" data-testid="catalog-logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 py-10" data-testid="catalog-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-overline">Pricing</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Repair catalog</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Pre-built repairs with default pricing. Pick from this list when adding items to a job — pricing auto-fills.
              Add your own to make quoting even faster.
            </p>
          </div>
          <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="new-template-btn">
            <Plus className="w-4 h-4" /> Add repair
          </button>
        </div>

        <div className="mt-6 max-w-md relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search repairs or categories…" className="input-default pl-9" data-testid="catalog-search-input" />
        </div>

        {loading ? (
          <div className="mt-10 text-center text-muted-foreground">Loading…</div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="mt-10 card-default p-10 text-center text-muted-foreground">No repairs found.</div>
        ) : (
          <div className="mt-8 space-y-8">
            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([cat, list]) => (
              <div key={cat}>
                <div className="flex items-baseline justify-between mb-3">
                  <h2 className="font-display font-bold text-xl tracking-tight">{cat}</h2>
                  <div className="text-xs text-muted-foreground font-mono-plex">{list.length} items</div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map(t => (
                    <div key={t.id} className="card-default p-4 hover:-translate-y-0.5 transition-all" data-testid={`template-${t.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 rounded-md bg-secondary grid place-items-center shrink-0">
                            {t.type === "part" ? <Package className="w-4 h-4" /> : <WrenchIcon className="w-4 h-4" />}
                          </div>
                          <div className="font-semibold truncate">{t.name}</div>
                        </div>
                        {t.technician_id ? (
                          <button onClick={() => remove(t.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`template-remove-${t.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <span title="Global preset" className="text-muted-foreground"><Lock className="w-3.5 h-3.5" /></span>
                        )}
                      </div>
                      {t.description && <div className="text-xs text-muted-foreground mt-2">{t.description}</div>}
                      {t.explainer_url && (
                        <a href={t.explainer_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-primary hover:underline" data-testid={`template-video-${t.id}`}>
                          <Video className="w-3 h-3" /> Explainer video
                        </a>
                      )}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                        <div className="font-mono-plex text-sm">
                          {t.default_quantity} × <span className="font-bold text-foreground">£{Number(t.default_price).toFixed(2)}</span>
                        </div>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          t.urgency === "critical" ? "bg-destructive/15 text-destructive" :
                          t.urgency === "high" ? "bg-warning/15 text-warning" :
                          t.urgency === "medium" ? "bg-primary/15 text-primary" :
                          "bg-secondary text-secondary-foreground"
                        }`}>
                          {t.urgency === "critical" && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
                          {t.urgency}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showNew && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowNew(false)}>
          <form onSubmit={create} onClick={(e) => e.stopPropagation()} className="bg-card rounded-lg border border-border shadow-xl w-full max-w-md p-6 space-y-4 animate-fade-up" data-testid="new-template-modal">
            <div>
              <div className="label-overline">Catalog</div>
              <h2 className="font-display text-2xl font-bold tracking-tight mt-1">New repair</h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-sm font-medium">Type</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {["part", "labor"].map(t => (
                    <button key={t} type="button" onClick={() => setDraft({ ...draft, type: t })}
                      className={`px-3 py-2 rounded-lg border text-sm font-semibold capitalize transition-all ${
                        draft.type === t ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"
                      }`} data-testid={`tpl-type-${t}`}>
                      {t === "part" ? <Package className="w-4 h-4 inline mr-2" /> : <WrenchIcon className="w-4 h-4 inline mr-2" />}
                      {t === "part" ? "Part" : "Labour"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Name *</label>
                <input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="input-default mt-1" data-testid="tpl-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Category</label>
                <input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="input-default mt-1" placeholder="e.g. Brakes" data-testid="tpl-category-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Default qty</label>
                <input type="number" step="0.1" min="0" value={draft.default_quantity} onChange={(e) => setDraft({ ...draft, default_quantity: e.target.value })} className="input-default mt-1" data-testid="tpl-qty-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Default price</label>
                <input type="number" step="0.01" min="0" value={draft.default_price} onChange={(e) => setDraft({ ...draft, default_price: e.target.value })} className="input-default mt-1" data-testid="tpl-price-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Urgency</label>
                <select value={draft.urgency} onChange={(e) => setDraft({ ...draft, urgency: e.target.value })} className="input-default mt-1" data-testid="tpl-urgency-input">
                  {URGENCIES.map(u => <option key={u.v} value={u.v}>{u.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Description</label>
                <textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="input-default mt-1" data-testid="tpl-description-input" />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Explainer video URL <span className="text-muted-foreground font-normal">(optional, YouTube)</span></label>
                <input type="url" value={draft.explainer_url} onChange={(e) => setDraft({ ...draft, explainer_url: e.target.value })} className="input-default mt-1" placeholder="https://youtu.be/…" data-testid="tpl-explainer-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowNew(false)} className="btn-outline" data-testid="tpl-cancel-btn">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="tpl-save-btn">Save to catalog</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
